import type { DataPoint } from "./types";

/**
 * The backend formats every `value_label` to the decimal count the admin panel
 * configured for the metric ("0.50" for a 2-decimal metric). The frontend never
 * knows that count directly, so it reads it back from the labels when it has to
 * derive a number of its own: an average row, a reference line, a trendline or a
 * pinned summary.
 */

const MAX_DECIMALS = 20;

/** Digits after the point in a backend value_label, or null when the label is not a plain number. */
export function decimalsFromLabel(label: string | null | undefined): number | null {
  if (label == null) return null;
  const match = /^-?\d+(?:\.(\d+))?$/.exec(label.trim());
  if (!match) return null;
  return match[1] ? match[1].length : 0;
}

/** The widest decimal count among a set of labels, or null when no label is a plain number. */
export function inferDecimalsFromLabels(labels: Iterable<string | null | undefined>): number | null {
  let decimals: number | null = null;
  for (const label of labels) {
    const d = decimalsFromLabel(label);
    if (d != null && (decimals == null || d > decimals)) decimals = d;
  }
  return decimals;
}

/** The decimal count of a series, read from the value_label its points carry. */
export function inferDecimals(points: readonly DataPoint[] | null | undefined): number | null {
  if (!points) return null;
  const labels: (string | undefined)[] = [];
  for (const p of points) {
    if (p == null || typeof p === "number") continue;
    labels.push(p.custom?.value_label);
  }
  return inferDecimalsFromLabels(labels);
}

function clampDecimals(decimals: number): number {
  if (!Number.isFinite(decimals)) return 0;
  return Math.min(MAX_DECIMALS, Math.max(0, Math.trunc(decimals)));
}

export function roundToDecimals(n: number, decimals: number): number {
  const factor = 10 ** clampDecimals(decimals);
  return Math.round((n + Number.EPSILON) * factor) / factor;
}

/** Round and pad, so 0.5 at 2 decimals reads "0.50". */
export function formatToDecimals(n: number, decimals: number): string {
  const d = clampDecimals(decimals);
  return roundToDecimals(n, d).toFixed(d);
}
